import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  RadioCards,
  ReasonDialog,
  ReasonField,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import { auditKeys } from "@amber/access/ui";
import { accessPreviewQuery, collectionCommands, collectionKeys } from "@amber/collection/ui";
import { operationsKeys } from "@amber/operations/ui";
import type { EvidenceRecord } from "../../contracts/index";
import { evidenceKeys } from "../keys";

const ACCESS_CHANGE = {
  invalidate: [collectionKeys.all, auditKeys.all, operationsKeys.all],
  invalidateOnConflict: [collectionKeys.all],
} as const;

export function RestrictDialog({ record, onClose }: { record: EvidenceRecord; onClose: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [mode, setMode] = useState<"restricted" | "deleted">("restricted");
  const [scope, setScope] = useState<"item" | "copies">("item");
  const toast = useToast();
  const client = useQueryClient();
  const preview = useQuery({ ...accessPreviewQuery(record.item.id, mode), enabled: step === 2 });
  const mutation = useCommandMutation({
    ...ACCESS_CHANGE,
    run: (csrf, text: string) =>
      collectionCommands.changeAccess(
        record.item.id,
        mode === "deleted" ? "delete" : "restrict",
        { reason: text },
        csrf,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: evidenceKeys.all });
      toast.show(mode === "deleted" ? "Deleted; kept and restorable by Admins" : "Restricted; visible to Admins only");
      onClose();
    },
  });
  const reason = useReason(isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : null);
  const units = record.turns.length;
  const others = record.communications.reduce((n, c) => n + c.copies.others, 0);
  const verb = mode === "deleted" ? "Delete" : "Restrict";
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={step === 1 ? "Restrict or delete…" : `${verb} this record?`}
      onSubmit={() => {
        if (step === 1) {
          setStep(2);
          return;
        }
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        step === 1 ? (
          <DialogFooter onClose={onClose} confirm="Continue" />
        ) : (
          <>
            <Button
              onClick={() => {
                setStep(1);
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant={mode === "deleted" ? "danger" : "primary"}
              aria-busy={mutation.isPending || undefined}
            >
              {mutation.isError ? "Try again" : verb}
            </Button>
          </>
        )
      }
    >
      {step === 1 ? (
        <>
          <DialogText>
            <b>{record.item.title}</b>
          </DialogText>
          <RadioCards
            label="Outcome"
            value={mode}
            onChange={setMode}
            options={[
              {
                value: "restricted",
                label: "Restrict",
                description:
                  "Hidden from exports, consumer reads and non-Admin workspace views. Admins still see it with a banner.",
              },
              {
                value: "deleted",
                label: "Delete",
                description:
                  "Hidden from all views, reads and exports. Kept in the archive and restorable from Settings › Deleted items.",
              },
            ]}
          />
          <RadioCards
            label="Scope"
            value={scope}
            onChange={(v) => {
              if (v === "item" || others > 0) setScope(v);
            }}
            options={[
              { value: "item", label: "This item", description: "The item, its source versions and captured files" },
              {
                value: "copies",
                label: "All copies of the communication",
                description: others > 0 ? `${String(others)} other copies` : "This communication has no other copies",
              },
            ]}
          />
        </>
      ) : (
        <>
          <DialogText>
            <b>{record.item.title}</b> · {verb} · this item
          </DialogText>
          <ul className="m-0 mb-1 list-disc pl-5 text-muted">
            <li>
              {units} evidence units in {record.communications.length} communication
              {record.communications.length === 1 ? "" : "s"} are withdrawn from reads and exports with a notice
            </li>
            <li>0 passage checks affected</li>
            {preview.data ? (
              <li>
                {preview.data.versions} source version{preview.data.versions === 1 ? "" : "s"} and{" "}
                {preview.data.artifacts} captured files
              </li>
            ) : null}
            {(preview.data?.effects ?? ["Preparing the preview…"]).map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
          <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
          <FailedCallout error={mutation.error} fieldsShown />
        </>
      )}
    </Dialog>
  );
}

export function RestoreDialog({ record, onClose }: { record: EvidenceRecord; onClose: () => void }) {
  const deleted = record.restriction?.state === "deleted";
  const verb = deleted ? "Restore" : "Lift restriction";
  return (
    <ReasonDialog
      title={`${verb}…`}
      confirm={verb}
      success={deleted ? "Restored" : "Restriction lifted"}
      onClose={onClose}
      run={(reason, csrf) => collectionCommands.changeAccess(record.item.id, "restore", { reason }, csrf)}
      invalidate={[...ACCESS_CHANGE.invalidate, evidenceKeys.all]}
      invalidateOnConflict={ACCESS_CHANGE.invalidateOnConflict}
      extra={
        <ul className="m-0 mb-1 list-disc pl-5 text-muted">
          <li>Removes only the {deleted ? "deletion" : "restriction"}; reads and exports receive a change</li>
          <li>Readiness will be reassessed under the current policy</li>
        </ul>
      }
    >
      <b>{record.item.title}</b>
    </ReasonDialog>
  );
}
